(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pageReady = new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

  const scrollToTarget = (target) => {
    const header = document.querySelector('.tsyrk-v2-header');
    const destination = () => {
      // offsetTop ignores reveal transforms, which previously moved the anchor
      // below the intended position. Re-measure while the sticky header shrinks.
      let top = 0;
      for (let element = target; element; element = element.offsetParent) top += element.offsetTop;
      const headerOffset = header ? header.getBoundingClientRect().height : 0;
      const gap = parseFloat(getComputedStyle(target).getPropertyValue('--tsyrk-anchor-gap')) || 0;
      return Math.max(0, top - headerOffset - gap);
    };
    if (reducedMotion.matches) {
      window.scrollTo(0, destination());
      return;
    }

    const start = window.scrollY;
    const duration = 900;
    const startedAt = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      window.scrollTo(0, start + (destination() - start) * eased);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  const reveal = (element) => {
    pageReady.then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => element.classList.add('is-visible'));
      });
    });
  };

  const showImmediately = (elements) => {
    elements.forEach((element) => element.classList.add('is-visible'));
  };

  const observe = (elements, rootMargin = '0px 0px -8% 0px') => {
    if (!elements.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      showImmediately(elements);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin, threshold: 0.08 });

    elements.forEach((element) => observer.observe(element));
  };

  const prepare = (root = document) => {
    document.documentElement.classList.add('tsyrk-motion-ready');

    root.querySelectorAll('.tsyrk-home-grid, .tsyrk-collection-grid').forEach((grid) => {
      const cards = [...grid.querySelectorAll('.tsyrk-grid-card:not([data-tsyrk-motion-bound])')];
      if (!cards.length) return;

      const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
      cards.forEach((card, index) => {
        card.dataset.tsyrkMotionBound = 'true';
        card.classList.add('tsyrk-motion-item');
        card.style.setProperty('--tsyrk-reveal-delay', `${30 + (index % columns) * 60}ms`);
      });
      observe(cards);
    });

    root.querySelectorAll('.tsyrk-index:not([data-tsyrk-motion-bound])').forEach((index) => {
      index.dataset.tsyrkMotionBound = 'true';
      const head = index.querySelector('.tsyrk-index__head');
      const rows = [...index.querySelectorAll('[data-tsyrk-index-row]')];
      if (head) {
        head.classList.add('tsyrk-motion-index-head');
        reveal(head);
      }
      rows.forEach((row) => row.classList.add('tsyrk-motion-index-row'));
      observe(rows, '0px 0px -4% 0px');
    });

    root.querySelectorAll('.tsyrk-about:not([data-tsyrk-motion-bound])').forEach((about) => {
      about.dataset.tsyrkMotionBound = 'true';
      const intro = [...about.querySelectorAll('.tsyrk-about__intro [data-tsyrk-about-reveal]')];
      const remaining = [...about.querySelectorAll('.tsyrk-about__showroom [data-tsyrk-about-reveal], [data-tsyrk-about-media]')];

      intro.forEach((item, index) => {
        item.classList.add('tsyrk-motion-about-item');
        item.style.setProperty('--tsyrk-about-delay', `${20 + index * 55}ms`);
        reveal(item);
      });

      remaining.forEach((item, index) => {
        item.classList.add('tsyrk-motion-about-item');
        item.style.setProperty('--tsyrk-about-delay', `${(index % 2) * 55}ms`);
      });

      observe(remaining, '0px 0px -5% 0px');
    });

    root.querySelectorAll('.tsyrk-collection-story:not([data-tsyrk-motion-bound])').forEach((story) => {
      story.dataset.tsyrkMotionBound = 'true';
      const intro = [...story.querySelectorAll('[data-tsyrk-story-reveal]')];
      const cards = [...story.querySelectorAll('[data-tsyrk-story-card]')];

      intro.forEach((item, index) => {
        item.classList.add('tsyrk-motion-story-intro');
        item.style.setProperty('--tsyrk-story-delay', `${20 + index * 70}ms`);
        reveal(item);
      });

      const columns = getComputedStyle(story.querySelector('.tsyrk-collection-story__gallery')).gridTemplateColumns.split(' ').length;
      cards.forEach((card, index) => {
        card.classList.add('tsyrk-motion-story-card');
        card.style.setProperty('--tsyrk-story-delay', `${30 + (index % columns) * 55}ms`);
      });
      observe(cards, '0px 0px -5% 0px');
    });

    root.querySelectorAll('.tsyrk-collections-list:not([data-tsyrk-motion-bound])').forEach((list) => {
      list.dataset.tsyrkMotionBound = 'true';
      const rows = [...list.querySelectorAll('[data-tsyrk-collections-row]')];

      rows.forEach((row, index) => {
        row.classList.add('tsyrk-motion-collections-row');
        row.style.setProperty('--tsyrk-collections-delay', `${20 + index * 45}ms`);
        reveal(row);
      });
    });

    root.querySelectorAll('.tsyrk-demi:not([data-tsyrk-motion-bound])').forEach((demi) => {
      demi.dataset.tsyrkMotionBound = 'true';
      const quote = demi.querySelector('[data-tsyrk-demi-reveal]');
      if (!quote) return;
      quote.classList.add('tsyrk-motion-demi');
      quote.style.setProperty('--tsyrk-page-delay', '20ms');
      reveal(quote);
    });

    root.querySelectorAll('.tsyrk-404:not([data-tsyrk-motion-bound])').forEach((page) => {
      page.dataset.tsyrkMotionBound = 'true';
      const items = [...page.querySelectorAll('[data-tsyrk-404-reveal]')];
      items.forEach((item, index) => {
        item.classList.add('tsyrk-motion-404');
        item.style.setProperty('--tsyrk-page-delay', `${20 + index * 70}ms`);
        reveal(item);
      });

      page.querySelectorAll('.tsyrk-404__navigation a').forEach((link, index) => {
        link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
          event.preventDefault();
          items.forEach((item, itemIndex) => item.style.setProperty('--tsyrk-exit-delay', `${(items.length - itemIndex - 1) * 35}ms`));
          page.classList.add('is-leaving');
          window.setTimeout(() => window.location.assign(link.href), reducedMotion.matches ? 0 : 300 + index * 20);
        });
      });
    });

    root.querySelectorAll('.tsyrk-v2-header:not([data-tsyrk-motion-bound])').forEach((header) => {
      header.dataset.tsyrkMotionBound = 'true';
      const syncHeader = () => {
        if (!header.isConnected) { window.removeEventListener('scroll', syncHeader); window.removeEventListener('resize', syncHeader); return; }
        header.classList.toggle('is-scrolled', innerWidth > 1024 && scrollY > 48);
      };
      window.addEventListener('scroll', syncHeader, { passive: true });
      window.addEventListener('resize', syncHeader, { passive: true });
      syncHeader();
      const headerObserver = new ResizeObserver(() => {
        if (!header.isConnected) { headerObserver.disconnect(); return; }
        document.documentElement.style.setProperty('--tsyrk-sticky-header-height', `${header.getBoundingClientRect().height}px`);
      });
      headerObserver.observe(header);
      const items = [...header.querySelectorAll('.tsyrk-header__desktop .tsyrk-header__logo, .tsyrk-header__desktop > nav, .tsyrk-header__utilities, .tsyrk-header__mobile > *, .tsyrk-header__secondary')];
      items.forEach((item, index) => {
        item.classList.add('tsyrk-motion-header-item');
        item.style.setProperty('--tsyrk-header-delay', `${20 + index * 40}ms`);
      });
      items.forEach(reveal);

      const menu = header.querySelector('.tsyrk-header__menu');
      if (menu) {
        const menuSummary = menu.querySelector('summary');
        const menuLabel = menuSummary.getAttribute('aria-label');
        const syncMenu = () => {
          document.documentElement.classList.toggle('tsyrk-menu-open', menu.open);
          menuSummary.setAttribute('aria-label', menu.open ? 'Close menu' : menuLabel);
        };
        menu.addEventListener('toggle', syncMenu);
        menu.addEventListener('keydown', (event) => { if (event.key === 'Escape') { menu.open = false; syncMenu(); menuSummary.focus(); } });
        syncMenu();
        menu.querySelectorAll('.tsyrk-header__drawer a').forEach((link) => {
          link.addEventListener('click', () => {
            menu.open = false;
            syncMenu();
          });
        });
      }
    });

    root.querySelectorAll('.tsyrk-v2-footer:not([data-tsyrk-motion-bound])').forEach((footer) => {
      footer.dataset.tsyrkMotionBound = 'true';
      const items = [...footer.children];
      const appointment = footer.querySelector('.tsyrk-footer__appointment');
      const appointmentArrow = footer.querySelector('.tsyrk-footer__appointment-arrow');
      items.forEach((item, index) => {
        item.classList.add('tsyrk-motion-footer');
        item.style.setProperty('--tsyrk-footer-delay', `${40 + index * 70}ms`);
      });
      // The final copyright line cannot cross a negative bottom root margin
      // when the document has already reached its maximum scroll position.
      observe(items, '0px');

      if (appointment && appointmentArrow) {
        const syncAppointmentArrow = () => {
          const isVisible = appointment.matches(':hover') || appointment.matches(':focus-within');
          appointment.classList.toggle('is-hovered', isVisible);
          appointmentArrow.style.clipPath = isVisible ? 'inset(0 0 0 0)' : '';
        };
        appointment.addEventListener('pointerenter', syncAppointmentArrow);
        appointment.addEventListener('pointerleave', syncAppointmentArrow);
        appointment.addEventListener('focusin', syncAppointmentArrow);
        appointment.addEventListener('focusout', syncAppointmentArrow);
      }
    });
  };

  prepare();
  document.addEventListener('shopify:section:load', (event) => prepare(event.target));

  if (!document.documentElement.hasAttribute('data-tsyrk-anchor-bound')) {
    document.documentElement.setAttribute('data-tsyrk-anchor-bound', 'true');

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;

      const url = new URL(link.href, window.location.href);
      if (!url.hash) return;

      if (url.pathname !== window.location.pathname) {
        if (url.pathname.endsWith('/pages/booking')) {
          event.preventDefault();
          try {
            sessionStorage.setItem('tsyrk-anchor', url.hash);
          } catch (error) {}
          document.documentElement.classList.add('tsyrk-anchor-transitioning', 'tsyrk-anchor-leaving');
          window.setTimeout(() => window.location.assign(`${url.pathname}${url.search}`), reducedMotion.matches ? 0 : 220);
        }
        return;
      }

      const target = document.querySelector(url.hash);
      if (!target || !target.closest('[data-tsyrk-about-page]')) return;

      event.preventDefault();
      history.pushState(null, '', url.hash);
      target.classList.add('tsyrk-anchor-target-incoming');
      scrollToTarget(target);
      requestAnimationFrame(() => target.classList.add('is-entering'));
      window.setTimeout(() => target.classList.remove('tsyrk-anchor-target-incoming', 'is-entering'), reducedMotion.matches ? 0 : 1000);
    });

    let requestedAnchor = window.location.hash;
    try {
      requestedAnchor = sessionStorage.getItem('tsyrk-anchor') || requestedAnchor;
    } catch (error) {}
    if (requestedAnchor) {
      const target = document.querySelector(requestedAnchor);
      if (target && target.closest('[data-tsyrk-about-page]')) {
        try {
          sessionStorage.removeItem('tsyrk-anchor');
        } catch (error) {}
        window.scrollTo({ top: 0, behavior: 'auto' });
        const splash = document.querySelector('[data-tsyrk-splash]');
        const splashDelay = splash ? (Number(splash.dataset.duration) || 2200) + 650 : 0;
        pageReady.then(() => {
          requestAnimationFrame(() => {
            document.documentElement.classList.remove('tsyrk-anchor-pending');
          });
          window.setTimeout(() => {
            history.replaceState(null, '', requestedAnchor);
            target.classList.add('tsyrk-anchor-target-incoming');
            scrollToTarget(target);
            window.setTimeout(() => target.classList.add('is-entering'), reducedMotion.matches ? 0 : 380);
            window.setTimeout(() => {
              target.classList.remove('tsyrk-anchor-target-incoming', 'is-entering');
              document.documentElement.classList.remove('tsyrk-anchor-transitioning', 'tsyrk-anchor-leaving');
            }, reducedMotion.matches ? 0 : 1300);
          }, reducedMotion.matches ? 0 : splashDelay + 420);
        });
      }
    }
  }
})();
